import { type FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PrepackageSearchForm } from 'shared/search/prepackage/PrepackageSearchForm';
import { ppDefaultState } from 'shared/search/prepackage/prepackageDefaultState';
import type { MonthDuration, MonthPickerMonth, PrepackageSearchState } from 'shared/search/types';
import { safeFormatISODate, safeParseISO } from 'shared/util/datetime-fns';
import type { PaleState, ThunkDispatchFunc } from '@/interfaces';
import { actions, actions as filterActions } from '@/state/filterActions';
import { fetchHotelResults } from '@/state/hotelResultActions';

type HotelPageSearchFormProps = {
    onSubmit: () => void;
};

export const HotelPageSearchForm = ({ onSubmit }: HotelPageSearchFormProps) => {
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const { travelMonths, duration, origin, numberOfAdults, agesOfChildren, departureDate, returnDate } = useSelector(
        (state: PaleState) => state.filters,
    );
    const [searchType, setSearchType] = useState<PrepackageSearchState['searchType']>(
        travelMonths.length !== 0 || !!duration ? 'flexible' : 'fixed',
    );

    const state: PrepackageSearchState = {
        flexibleDeparture: departureDate?.flexibility ?? ppDefaultState.flexibleDeparture,
        departureDate: safeParseISO(departureDate?.date, null),
        returnDate: safeParseISO(returnDate?.date, null),
        searchType,
        travelMonths,
        duration: { from: duration?.[0] ?? ppDefaultState.duration.from, to: duration?.[1] ?? ppDefaultState.duration.to },
        originAirport: origin,
        numberOfAdults: numberOfAdults ?? ppDefaultState.numberOfAdults,
        agesOfChildren: agesOfChildren ?? ppDefaultState.agesOfChildren,
    };

    return (
        <PrepackageSearchForm
            ready={true}
            state={state}
            invalid={{}}
            validationErrors={{}}
            onSubmit={(event: FormEvent<HTMLFormElement>): void => {
                event.preventDefault();
                onSubmit();
                dispatch(fetchHotelResults());
            }}
            onSearchTypeChange={(searchType: PrepackageSearchState['searchType']): void => {
                setSearchType(searchType);
                if (searchType === 'fixed') {
                    dispatch(actions.setIgnoredFilters(['duration', 'travelMonths']));
                } else {
                    dispatch(actions.setIgnoredFilters(['departureDate', 'returnDate']));
                }
            }}
            onOriginAirportChange={(originAirport: string): void => {
                dispatch(actions.changeOrigin(originAirport));
            }}
            onToggleTravelMonth={(month: MonthPickerMonth): void => {
                dispatch(actions.toggleTravelMonth(month));
            }}
            onDurationChange={(duration: MonthDuration): void => {
                dispatch(actions.changeDuration([duration.from, duration.to]));
            }}
            onDatesChange={(dates: { startDate: Date | null; endDate: Date | null }): void => {
                dispatch(actions.setDates({ startDate: safeFormatISODate(dates.startDate), endDate: safeFormatISODate(dates.endDate) }));
            }}
            onPaxChange={(adults: number, agesOfChildren: number[]): void => {
                dispatch(filterActions.changePaxCombination({ numberOfAdults: adults, agesOfChildren: agesOfChildren }));
            }}
            compactTop={true}
        />
    );
};
